/**
 * What a voucher does to a karcis session, as arithmetic.
 *
 * The karcis booth had no voucher entry at all — Dhafin asked for one on
 * 2026-09-22 ("foto karcis udah bisa pake kode promo belom?") — and the four
 * cases below are the reason this is a module rather than four lines inside a
 * click handler. Each is a different shape of transaction record, and getting
 * one wrong is a revenue figure that is quietly wrong forever:
 *
 *   free        the code covers the whole Rp 5.000 — no gateway quote at all
 *   partial     a discount that leaves something to pay — re-quote for the rest
 *   cash        the operator already holds the money; revenue IS the price
 *   invalid     nothing changes and the QR on screen stays live
 *
 * The one that bites is `cash`. A cash voucher is not a discount: the guest
 * paid, in cash, at the desk. Recording it as a zero-rupiah session would show
 * the booth giving karcis away. So `amount` is the full price and `discount` is
 * zero, which is also how the Bahela checkout has always recorded them
 * (components/bahela/PromoModal).
 *
 * Note `discount` here is RUPIAH, always. The Bahela checkout stores a
 * percentage discount as a negative number and re-derives the rupiah later;
 * this booth writes the transaction itself, so it writes the money.
 */

/** The shape `/api/promos/apply` answers with. Only the fields this flow uses. */
export interface PromoApplyResponse {
  isValid?: boolean
  message?: string
  discount?: number
  type?: 'fixed' | 'percentage'
  promoId?: string
  isCash?: boolean
  value?: number
}

export interface KarcisVoucherApplication {
  /** Uppercased, as stored on the session and on the Promo document. */
  code: string
  voucherId?: string
  voucherType: 'fixed' | 'percentage' | 'cash'
  voucherValue?: number
  /** Rupiah off the karcis price. Zero for a cash voucher — see above. */
  discount: number
  /** Still owed through QRIS. Zero means skip the gateway entirely. */
  payable: number
  /** Revenue to record for this session. */
  amount: number
  /** True when nothing is left to pay and the session settles on the spot. */
  coversSession: boolean
}

/**
 * Turn an apply response into the numbers this session should record, or null
 * if the code did nothing.
 *
 * `discount` is clamped to the price rather than trusted: nothing stops an
 * operator making a Rp 50.000 fixed voucher, and the karcis costs Rp 5.000. Un
 * clamped that is a negative payable, which reaches the gateway as a negative
 * charge and the ledger as negative revenue.
 */
export function readKarcisVoucher(
  price: number,
  code: string,
  data: PromoApplyResponse | null | undefined
): KarcisVoucherApplication | null {
  if (!data?.isValid) return null

  const normalizedCode = code.trim().toUpperCase()
  if (!normalizedCode) return null

  const base = {
    code: normalizedCode,
    voucherId: typeof data.promoId === 'string' ? data.promoId : undefined,
    voucherValue: typeof data.value === 'number' ? data.value : undefined
  }

  if (data.isCash) {
    return {
      ...base,
      voucherType: 'cash',
      discount: 0,
      payable: 0,
      amount: Math.round(price),
      coversSession: true
    }
  }

  const raw = Number(data.discount)
  const discount = Math.min(
    Math.max(Number.isFinite(raw) ? Math.round(raw) : 0, 0),
    Math.round(price)
  )
  const payable = Math.round(price) - discount

  return {
    ...base,
    voucherType: data.type === 'fixed' ? 'fixed' : 'percentage',
    discount,
    payable,
    amount: payable,
    coversSession: payable <= 0
  }
}

/**
 * What the guest still owes, with or without a voucher.
 *
 * Exists so the payment path has ONE source for the figure it quotes, records
 * and reconciles. With no voucher this is the price, unchanged — which is what
 * keeps the analog-TV karcis, where no code can ever be entered, byte-for-byte
 * on the behaviour it shipped with.
 */
export function payableKarcisAmount(
  price: number,
  voucher: KarcisVoucherApplication | null
): number {
  if (!voucher) return Math.round(price)
  return Math.max(0, voucher.payable)
}
